import React, { useEffect, useState } from 'react';
import { Upload, Button, message } from 'antd';
import { IconPaperclip } from 'components/icons/Paperclip';
import { COLORS } from 'utils/colors';

export const FileUpload = ({ onFileSelect, onFileRemove, className, files, ...restProps }) => {
  const getDocumentName = (file, index) => {
    const explicitName = file?.name || file?.filename || file?.original_name || file?.title;

    if (explicitName) return explicitName;

    if (file?.url) {
      try {
        const fromUrl = decodeURIComponent(file.url.split('?')[0].split('/').pop() || '');
        if (fromUrl) return fromUrl;
      } catch (error) {
        const fromUrl = file.url.split('?')[0].split('/').pop() || '';
        if (fromUrl) return fromUrl;
      }
    }

    return `Attached Document ${index + 1}`;
  };

  const normalizeFiles = (documents = []) =>
    documents.map((file, index) => {
      if (file?.originFileObj) {
        return file;
      }

      return {
        ...file,
        uid: String(file?.uid ?? file?.id ?? file?.url ?? `existing-doc-${index}`),
        name: getDocumentName(file, index),
        status: file?.status || 'done',
      };
    });

  const hasExternalFiles = Array.isArray(files);
  const [fileList, setFileList] = useState(normalizeFiles(hasExternalFiles ? files : []));

  useEffect(() => {
    if (hasExternalFiles) {
      setFileList(normalizeFiles(files));
    }
  }, [files]);

  const beforeUpload = (file) => {
    const isSupportedType =
      file.type === 'application/pdf' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/png';

    if (!isSupportedType) {
      message.error('You can only upload PDF, DOCX, JPEG, or PNG files!');
      return Upload.LIST_IGNORE;
    }

    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error('File must be smaller than 50MB!');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleChange = ({ file, fileList }) => {
    setFileList(fileList);

    const selectedNewFiles = fileList
      .filter((item) => !!item.originFileObj)
      .map((item) => item.originFileObj);

    if (file.status === 'removed') {
      // If the removed file has no originFileObj it was an existing backend document
      if (!file.originFileObj && onFileRemove) {
        onFileRemove(file);
      }
    }

    onFileSelect(selectedNewFiles);
  };

  return (
    <Upload
      fileList={fileList}
      beforeUpload={beforeUpload}
      onChange={handleChange}
      className={`${className}`}
      {...restProps}
    >
      <Button className="w-full border-none flex justify-between h-[44px] text-base font-medium base-block rounded-lg">
        {fileList.length > 0 ? 'Add Another Document' : 'Attach a Document'}
        <IconPaperclip color={COLORS.blue} />
      </Button>
    </Upload>
  );
};
